import React, { useState } from 'react';

interface MobileTableCardProps<T> {
  record: T;
  columns: {
    title: string;
    dataIndex: string;
    render?: (value: any, record: T) => React.ReactNode;
    isOperation?: boolean;
  }[];
  t?: (key: string) => string;
}

export function MobileTableCard<T extends { [key: string]: any }>({
  record,
  columns,
  t,
}: MobileTableCardProps<T>) {
  const [expanded, setExpanded] = useState(false);

  // Split columns into normal and operation
  const normalColumns = columns.filter(col => !col.isOperation);
  const operationColumn = columns.find(col => col.isOperation);

  // First three fields for collapsed view
  const collapsedFields = normalColumns.slice(0, 3);

  return (
    <div className="p-3 text-sm mb-3 rounded-lg text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-['Inter'] bg-button dark:bg-dark-button">
      {/* Collapsed header: first 3 fields + expand button */}
      <div className="grid grid-cols-[95%_5%] items-center">
        {!expanded && (
            <div className="grid grid-cols-3 gap-x-3 gap-y-3 text-xs">
                {collapsedFields.map(col => (
                    <div key={col.dataIndex} className="flex flex-col">
                    <span className="leading-none text-normal dark:text-dark-normal font-medium mb-1">
                        {col.title}
                    </span>
                    <span className="leading-none text-sel-normal dark:text-dark-sel-normal">
                        {col.render
                        ? col.render(record[col.dataIndex], record)
                        : record[col.dataIndex]}
                    </span>
                    </div>
                ))}
            </div>
        )}
        {!expanded && (
            <button
                onClick={() => setExpanded(true)}
                className="justify-self-end hover:bg-button-hover dark:hover:bg-dark-button-hover rounded transition-colors duration-200"
            >
                <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-normal dark:text-dark-normal transition-transform duration-300"
                viewBox="0 0 20 20"
                fill="currentColor"
                >
                <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                />
                </svg>
            </button>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <>
          <div className="grid grid-cols-[95%_5%] items-center">
            <div className="grid grid-cols-3 gap-x-3 gap-y-3 text-xs">
              {normalColumns.map(col => (
                <div key={col.dataIndex} className="flex flex-col">
                  <span className="leading-none text-normal dark:text-dark-normal font-medium mb-1">
                    {col.title}
                  </span>
                  <span className="leading-none text-sel-normal dark:text-dark-sel-normal">
                    {col.render
                      ? col.render(record[col.dataIndex], record)
                      : record[col.dataIndex]}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Divider */}
          <div className="my-3 border-t border-normal dark:border-dark-normal"></div>
          {/* Operation buttons in expanded view */}
          {operationColumn ? (
            <div className="grid grid-cols-[95%_5%] items-center">
                {operationColumn.render
                    ? operationColumn.render(record[operationColumn.dataIndex], record)
                    : record[operationColumn.dataIndex]}
              <button
                onClick={() => setExpanded(false)}
                className="justify-self-end hover:bg-button-hover dark:hover:bg-dark-button-hover rounded transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-normal dark:text-dark-normal transition-transform duration-300 rotate-180"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-[95%_5%] items-center">
              <div/>
              <button
                onClick={() => setExpanded(false)}
                className="justify-self-end hover:bg-button-hover dark:hover:bg-dark-button-hover rounded transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-normal dark:text-dark-normal transition-transform duration-300 rotate-180"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}